import dayjs from "dayjs";

/**
 * Recognises the appointment rejections that PRET-1845 and PRET-1837 define as *expected*
 * outcomes rather than failures, so the UI can show them beside the fields instead of behind
 * the alert modal.
 *
 * The alert stays the catch-all for the genuinely unexpected — a duplicate email, a request
 * that fell over. Routing an ordinary validation outcome through it makes the coordinator
 * dismiss a dialog before they can read what to change.
 *
 * Kept here rather than in each modal because the invite modal and the appointment modal both
 * classify the same rejections, and two copies of the code list drift.
 */

/** Error codes thrown by the backend's appointments service. */
export const APPOINTMENT_VALIDATION_CODES = [
  "APPOINTMENT_ON_RELEASE_DATE",
  "APPOINTMENT_DATE_OCCUPIED",
  "ENROLLMENT_HAS_NO_ALLOCATION",
] as const;

/**
 * Returns the message to show inline, or null when the caller should fall back to the alert.
 *
 * @param error a rejection from `transformAxiosError`, shaped `{ message, error: ErrorType }`
 * @param fallback used when the backend named a code but no message
 */
export const readAppointmentValidationError = (
  error: unknown,
  fallback: string
): string | null => {
  const envelope = (
    error as { error?: { errorCode?: string; errorMessage?: string } } | null
  )?.error;

  if (
    !envelope?.errorCode ||
    !(APPOINTMENT_VALIDATION_CODES as readonly string[]).includes(
      envelope.errorCode
    )
  ) {
    return null;
  }

  return envelope.errorMessage?.trim() || fallback;
};

/**
 * PRET-1917 — the release-date revalidation refusal, which is a different shape of outcome.
 *
 * Deliberately NOT in `APPOINTMENT_VALIDATION_CODES` above. Those codes are answered by a message;
 * this one is answered by a whole UI state — design 8b turns the release-date field red, states the
 * conflict beneath it, and opens a correction section pre-filled from the appointment being
 * replaced. So the caller needs the `cause` payload, not a string.
 *
 * It cannot occur on the create path (there is no existing appointment to invalidate), which is why
 * only the edit modal reads it.
 */
export const FIRST_APPOINTMENT_INVALID_AFTER_RELEASE =
  "FIRST_APPOINTMENT_INVALID_AFTER_RELEASE";

/**
 * The existing appointment the backend refused to leave in place.
 *
 * The timestamps are the project's naive-local-with-a-fake-Z form: `2026-07-25T10:30:00Z` means
 * 10:30 as the participant experiences it. Read them by STRIPPING the Z, never by converting.
 */
export type FirstAppointmentConflict = {
  releaseDate?: string;
  firstAppointmentStart?: string;
  firstAppointmentEnd?: string;
};

/**
 * Returns the conflict detail when the backend blocked the save on PRET-1917 AC1, else null.
 *
 * An empty object is returned rather than null when the code is present but `cause` is missing — the
 * save WAS blocked, and the correction section has to open either way. Only the restated line
 * degrades.
 */
export const readFirstAppointmentConflict = (
  error: unknown
): FirstAppointmentConflict | null => {
  const envelope = (
    error as { error?: { errorCode?: string; cause?: unknown } } | null
  )?.error;

  if (envelope?.errorCode !== FIRST_APPOINTMENT_INVALID_AFTER_RELEASE) {
    return null;
  }

  return (envelope.cause as FirstAppointmentConflict) ?? {};
};

/**
 * Reads one of the naive-local-with-a-fake-Z timestamps back as WALL TIME.
 *
 * Stripping the `Z` and parsing locally reproduces the digits exactly as stored, which is the whole
 * point: converting would tell a coordinator in one zone that an appointment agreed for 10:30 is at
 * 05:30. The backend strips it the same way (`participants.controller.ts`), as do
 * `appointmentUiStatus.util.ts` and the mobile `accountCreatedModel.ts`.
 */
export const readWallTime = (iso?: string): dayjs.Dayjs | null =>
  iso ? dayjs(String(iso).replace(/Z$/, "")) : null;
